import React, { useState, useContext, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import { FaSearch, FaChartBar, FaUserFriends, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const REGISTROS_POR_PAGINA = 8;

export default function ReporteAsistencia() {
  const { token } = useContext(AuthContext);

  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const generarReporte = async () => {
    if (!inicio || !fin) {
      mostrarToast("Selecciona ambas fechas", "error");
      return;
    }

    setLoading(true);
    setPagina(1);

    try {
      const res = await fetch(
        `/api/reportes/asistencia?fecha_inicio=${inicio}&fecha_fin=${fin}`,
        {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await res.json();
      setData(Array.isArray(result) ? result : []);

    } catch (error) {
      console.error(error);
      mostrarToast("Error generando el reporte", "error");
    }

    setLoading(false);
  };

  const dataFiltrada = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return data;
    return data.filter((d) =>
      `${d.nombre} ${d.paterno || ""}`.toLowerCase().includes(termino)
    );
  }, [data, busqueda]);

  const totalPaginas = Math.ceil(dataFiltrada.length / REGISTROS_POR_PAGINA);
  const inicioIdx = (pagina - 1) * REGISTROS_POR_PAGINA;
  const dataPagina = dataFiltrada.slice(inicioIdx, inicioIdx + REGISTROS_POR_PAGINA);
  const inicioRango = dataFiltrada.length === 0 ? 0 : inicioIdx + 1;
  const finRango = Math.min(inicioIdx + REGISTROS_POR_PAGINA, dataFiltrada.length);

  return (
    <div className="p-6 bg-slate-50">

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Reporte de Asistencia</h1>
        <p className="text-sm text-slate-500 mt-0.5">Consulta la asistencia a visitación y club por rango de fechas.</p>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Fecha inicio</label>
          <input
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Fecha fin</label>
          <input
            type="date"
            value={fin}
            onChange={(e) => setFin(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <button
          onClick={generarReporte}
          disabled={loading}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaChartBar className="text-xs" /> {loading ? "Generando..." : "Generar"}
        </button>
      </div>

      {/* BUSCADOR */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
        <div className="relative max-w-sm">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por nombre..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPagina(1);
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Nombre</th>
                <th className="px-4 py-3 text-right font-semibold">Visitación</th>
                <th className="px-4 py-3 text-right font-semibold">Club</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-slate-400">Cargando...</td>
                </tr>
              ) : dataPagina.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-slate-400">
                    {data.length === 0 ? "Genera un reporte para ver resultados" : "Sin resultados"}
                  </td>
                </tr>
              ) : (
                dataPagina.map((d) => (
                  <tr key={d.id_miembro} className="border-t border-slate-100 hover:bg-slate-50 transition">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2 font-medium text-slate-800">
                        <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                          <FaUserFriends className="text-xs" />
                        </span>
                        {d.nombre} {d.paterno}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{d.total_visitacion}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{d.total_club}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-indigo-600">{d.total}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {dataFiltrada.length === 0
            ? "Sin resultados"
            : `Mostrando ${inicioRango} a ${finRango} de ${dataFiltrada.length} resultados`}
        </span>

        {totalPaginas > 1 && (
          <div className="flex items-center gap-2">
            <button
              disabled={pagina === 1}
              onClick={() => setPagina(pagina - 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
            >
              <FaChevronLeft className="text-xs" />
            </button>

            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">
              {pagina}
            </span>

            <button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(pagina + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
